import { Fragment } from 'react';
import { Link } from '../router/Link';
import { AddToQueue } from '../queue/AddToQueue';
import { useCopy } from '../strings/useCopy';
import {
  BlueskyMarkIcon,
  cx,
  DiscordMarkIcon,
  FacebookMarkIcon,
  InstagramMarkIcon,
  PatreonMarkIcon,
  RedditMarkIcon,
  TikTokMarkIcon,
  TwitchMarkIcon,
  VisiousMarkIcon,
  XSiteMarkIcon,
  YouTubeMarkIcon,
} from '../ui';
import { useInlineChannel, useInlineVideo, useInstanceConfig } from './inlineMeta';
import { inlineQueueCandidate, siteName, type LinkMark, type RichNodeView } from './model';
import styles from './richText.module.css';

/**
 * The first renderer of R8's node tree — nothing has put a `RichNode` on screen before this file.
 *
 * It is a `switch` over `node.t` and nothing else: no HTML string, no `dangerouslySetInnerHTML`,
 * and no URL building (every href arrived resolved from `model.ts`, per R13). That is the whole
 * point of the tree — the same switch is a `when` in Compose and a set of `<Text>`s in RN.
 *
 * **The one arm with a decision in it is `link`.** Its href is uploader-controlled text that
 * reached us through two rewriting layers, so:
 *
 * - `rel="noopener noreferrer"` — `noopener` because `target="_blank"` otherwise hands the opened
 *   page a live `window.opener` back into ours, and `noreferrer` because a video description is not
 *   entitled to know which page the reader came from.
 * - `target="_blank"` — leaving the page mid-video would destroy the player and lose the position.
 *   That is a taste call, and it is the one that costs the reader least.
 * - The label is the URL itself, never the uploader's display text. `parse/richText.ts` derives it
 *   from the href on purpose: YouTube's own shortened `example.com/…` labels are an
 *   invitation to point a familiar-looking label at somewhere else entirely.
 *
 * The scheme is *not* checked here. The parser already refuses any href that is not `http(s)` or an
 * instance route, so `javascript:` never becomes a `link` node in the first place — one gate, at
 * the boundary, rather than a second one here that could drift away from it.
 *
 * **An address in prose is drawn as what it points at**, which is the other half of the tree's
 * payoff. A `youtube.com/watch?v=…` becomes the video's title with the queue's own button after it,
 * a `youtube.com/@name` becomes the handle, a `/channel/UC…` becomes the channel's name — and each of
 * them opens here rather than on YouTube. Where this build has no such page the link stays outbound
 * and says so with YouTube's mark. Nothing waits on a request to render: until a title arrives the
 * link is the address the author wrote, and it works.
 */

export interface RichTextProps {
  readonly nodes: readonly RichNodeView[];
  /**
   * A `timestamp` node seeks *this* video rather than navigating — hence a callback, not an href.
   *
   * Optional since 2d, because a channel description has nowhere to seek. It is not merely unused
   * there: `parseDescriptionNodes` gates timestamps on a duration, and a channel record has none, so a
   * `timestamp` node cannot exist in that tree at all. Without the callback the arm renders as plain
   * text, which is what a "1:30" in a channel description is.
   */
  readonly onSeek?: (seconds: number) => void;
  readonly className?: string;
}

export function RichText({ nodes, onSeek, className }: RichTextProps) {
  const words = useCopy().text.richText;
  return (
    <span className={className}>
      {nodes.map((node, index) => {
        switch (node.t) {
          case 'text':
            return <Fragment key={index}>{node.s}</Fragment>;

          case 'br':
            return <br key={index} />;

          case 'link': {
            const anchor = (
              <a
                className={styles.link}
                href={node.href}
                title={node.mark === null ? undefined : words.opensOn(siteName(node.mark))}
                target="_blank"
                rel="noopener noreferrer"
              >
                {node.s}
              </a>
            );
            // An unmarked link is exactly the element it has always been — no wrapper, so nothing
            // about how an ordinary URL sits in a paragraph changes.
            return node.mark === null ? (
              <Fragment key={index}>{anchor}</Fragment>
            ) : (
              <span key={index} className={styles.marked}>
                <Mark kind={node.mark} />
                {anchor}
              </span>
            );
          }

          case 'timestamp':
            // Without a seek target this is text, not a dead button — the same rule the `route` arm
            // below applies to an unresolvable mention.
            return onSeek === undefined ? (
              <Fragment key={index}>{node.s}</Fragment>
            ) : (
              <button
                key={index}
                type="button"
                className={styles.timestamp}
                onClick={() => onSeek(node.seconds)}
              >
                {node.s}
              </button>
            );

          case 'video':
            return <VideoLink key={index} node={node} />;

          case 'channel':
            return <ChannelLink key={index} node={node} />;

          case 'route': {
            // A hashtag or mention whose target could not be resolved stays legible as text rather
            // than becoming a link that goes nowhere (`mentionRoute` returns `null` for those).
            if (node.href === null) return <Fragment key={index}>{node.s}</Fragment>;
            const link = (
              <Link
                className={styles.link}
                href={node.href}
                title={node.mark === null ? undefined : words.opensHere}
              >
                {node.s}
              </Link>
            );
            // A `#hashtag` or an `@mention` was written as prose rather than as an address, so it
            // keeps the shape it has always had — see `LinkMark` for why those carry no mark.
            return node.mark === null ? (
              <Fragment key={index}>{link}</Fragment>
            ) : (
              <span key={index} className={styles.marked}>
                <Mark kind={node.mark} />
                {link}
              </span>
            );
          }
        }
      })}
    </span>
  );
}

/**
 * Whose page the link beside this goes to. `aria-hidden`, because the sentence it stands for is on
 * the link itself as a `title` — a mark announced separately would put "image, Visious" between the
 * words of a description.
 *
 * The accent is the app's whole vocabulary for "something you can act on here", so it belongs to the
 * one mark that stays in the app; every mark that leaves is muted, whichever site it belongs to. That
 * is also why none of the nine carries its brand colour: ten coloured badges in a paragraph is a
 * sticker sheet, and the shape is what a reader recognises anyway.
 */
function Mark({ kind }: { readonly kind: LinkMark }) {
  if (kind === null) return null;
  if (kind === 'visious') return <VisiousMarkIcon className={cx(styles.mark, styles.markHere)} />;
  const away = cx(styles.mark, styles.markAway);
  switch (kind) {
    case 'youtube':
      return <YouTubeMarkIcon className={away} />;
    case 'twitch':
      return <TwitchMarkIcon className={away} />;
    case 'patreon':
      return <PatreonMarkIcon className={away} />;
    case 'x':
      return <XSiteMarkIcon className={away} />;
    case 'facebook':
      return <FacebookMarkIcon className={away} />;
    case 'instagram':
      return <InstagramMarkIcon className={away} />;
    case 'tiktok':
      return <TikTokMarkIcon className={away} />;
    case 'bluesky':
      return <BlueskyMarkIcon className={away} />;
    case 'reddit':
      return <RedditMarkIcon className={away} />;
    case 'discord':
      return <DiscordMarkIcon className={away} />;
  }
}

/**
 * A linked video: its **title** where the author wrote an address, and the queue's own button after
 * it — the same button a grid card carries, editing the same list.
 *
 * Both wait on the record, and neither blocks the paragraph. Until it arrives the link is the address
 * the author wrote and already navigates; the title replaces it in place, and the button appears with
 * it because a queue row needs a title to be a row at all (`inlineQueueCandidate`).
 *
 * The `observe` ref is what keeps this cheap: a video record is 89–262 KB, so nothing is asked for
 * until the link is on screen (`inlineMeta.ts`).
 */
function VideoLink({ node }: { readonly node: Extract<RichNodeView, { t: 'video' }> }) {
  const words = useCopy().text.richText;
  const { meta, observe } = useInlineVideo(node.videoId);
  const config = useInstanceConfig();

  return (
    <span className={styles.marked} ref={observe}>
      <Mark kind="visious" />
      <Link className={styles.link} href={node.href} title={words.opensHere}>
        {meta?.title ?? node.s}
      </Link>
      {meta === null ? null : (
        <AddToQueue
          variant="ghost"
          item={inlineQueueCandidate({
            videoId: node.videoId,
            href: node.href,
            title: meta.title,
            author: meta.author,
            authorId: meta.authorId,
            lengthSeconds: meta.lengthSeconds,
            config,
          })}
        />
      )}
    </span>
  );
}

/**
 * A channel linked by its id, drawn as its **name**.
 *
 * Not its handle, and that is the API rather than a choice: `/api/v1/channels/{ucid}` returns twenty
 * fields and none of them is a handle, so a UCID cannot be turned back into one. A
 * `youtube.com/@name` link needs none of this — the handle is in the path and core labels the node
 * with it before this file sees it.
 *
 * The `@` goes in front of the name for the reason core puts one in front of every other channel
 * label: it is what makes a word in the middle of a sentence read as a channel rather than as prose.
 * It is not added to the fallback, which is the whole URL and already says what it is.
 */
function ChannelLink({ node }: { readonly node: Extract<RichNodeView, { t: 'channel' }> }) {
  const words = useCopy().text.richText;
  const { meta, observe } = useInlineChannel(node.channelId);

  return (
    <span className={styles.marked} ref={observe}>
      <Mark kind="visious" />
      <Link className={styles.link} href={node.href} title={words.opensHere}>
        {meta === null ? node.s : `@${meta.name}`}
      </Link>
    </span>
  );
}
